import { Divider, Flex } from "styled-system/jsx";
import { Text, ExpandableInput, Heading, Icon, Spinner } from "@ttc3k/trekker";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { Search } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";

interface BundleHeaderProps {
  searchProps?: {
    value: string;
    onValueChange: (e: ChangeEvent<HTMLInputElement>) => void;
  };
  count?: number;
  title?: string;
  Icon?: React.ForwardRefExoticComponent<
    Omit<React.SVGProps<SVGSVGElement>, "ref"> &
      React.RefAttributes<SVGSVGElement>
  >;
  loading?: boolean;
}

export const BundleHeader = ({
  searchProps,
  count,
  Icon: IconElement,
  title,
  loading,
}: BundleHeaderProps) => {
  const { t } = useTranslation();

  const [showTitle, setShowTitle] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]: IntersectionObserverEntry[]) => {
        setShowTitle(!entry.isIntersecting);
      },
      {
        root: document.getElementById("app-layout__main-content"),
        rootMargin: "-16px",
        threshold: 0.9,
      },
    );
    observer.observe(el);

    return () => observer.unobserve(el);
  }, [containerRef]);

  return (
    <Flex
      ref={containerRef}
      pb={"150"}
      borderBottomWidth={"2px"}
      borderBottomColor={"border.light"}
      justifyContent={"flex-end"}
      alignItems={"center"}
      gap={"150"}
      position="sticky"
      top="-16px"
      pt="16px"
      zIndex="10"
      background={"bg.wa.light"}
    >
      <AnimatePresence mode="wait">
        {showTitle && title && (
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: "100%" }}
            exit={{ opacity: 0, width: 0 }}
            transition={{ duration: 0.2 }}
            style={{ transformOrigin: "top left" }}
          >
            <Heading
              size={"h5"}
              display="flex"
              flexDir="row"
              alignItems="center"
              gap="150"
              whiteSpace="nowrap"
            >
              {IconElement && <Icon Element={IconElement} size="lg" />}
              {title}
            </Heading>
          </motion.div>
        )}
      </AnimatePresence>

      {searchProps && (
        <>
          <ExpandableInput
            placement={"right"}
            label={`${t("core:SEARCH")}...`}
            placeholder={`${t("core:SEARCH")}...`}
            Icon={Search}
            value={searchProps.value}
            onChange={searchProps.onValueChange}
            onClose={() =>
              /* Pass a fake event to the onValueChange with an empty string for the value */
              searchProps.onValueChange({
                target: { value: "" } as HTMLInputElement,
                currentTarget: { value: "" } as HTMLInputElement,
              } as ChangeEvent<HTMLInputElement>)
            }
          />
          <Divider
            orientation={"vertical"}
            thickness={"2px"}
            height={"18px"}
            color={"border.light"}
          />
        </>
      )}
      {count! >= 0 &&
        (loading ? (
          <Spinner w="10px" h="10px" mx={100} />
        ) : (
          <Text visual={"smallSemiBold"} minW={"fit-content"}>
            {count} {t("directory:PAGE.BUNDLE_HEADER.COUNT", { count })}
          </Text>
        ))}
    </Flex>
  );
};
